import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Flex, Popconfirm } from 'antd';
import { Fragment, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  approvalAdditionalWorkingTimeTickets,
  cancelAdditionalWorkingTimeTicket,
  cancelApprovalAdditionalWorkingTimeTicket
} from 'src/apis/hr_ticket/additionalWorkingTimeTicket.api';
import {
  approvalAdvancePaymentTickets,
  cancelAdvancePaymentTicket,
  cancelApprovalAdvancePaymentTickets,
  printAdvancePaymentTicket
} from 'src/apis/hr_ticket/advancePaymentTicket.api';
import {
  approvalBusinessTripTickets,
  cancelApprovalBusinessTripTickets,
  cancelBusinessTripTicket,
  hrConfirmBusinessTripTicket,
  printBusinessTripTicket
} from 'src/apis/hr_ticket/businessTripTicket.api';
import {
  approvalEquipmentSupplyRequestTickets,
  cancelApprovalEquipmentSupplyRequestTickets,
  cancelEquipmentSupplyRequestTicket,
  printEquipmentSupplyRequestTicket
} from 'src/apis/hr_ticket/equipmentSupplyRequestTicket.api';
import {
  approvalTickets,
  cancelApprovalTickets,
  cancelTicket,
  printTicket
} from 'src/apis/hr_ticket/expenseTrackingRequestTicket.api';
import { approvalLeavePackage, cancelApprovalLeavePackage } from 'src/apis/hr_ticket/leavePackage.api';
import {
  approvalLeaveTickets,
  cancelApprovalLeaveTicket,
  cancelLeaveTicket,
  hrConfirmLeaveTicket,
  printLeaveTicket
} from 'src/apis/hr_ticket/leaveTicket.api';
import {
  approvalOvertimeTickets,
  cancelApprovalOvertimeTickets,
  cancelOvertimeTicket,
  duplicateOvertimeTicket,
  hrConfirmOvertimeTicket,
  printOvertimeTicket
} from 'src/apis/hr_ticket/overtimeTicket.api';
import {
  approvalPurchaseRequestTickets,
  cancelApprovalPurchaseRequestTickets,
  cancelPurchaseRequestTicket,
  printPurchaseRequestTicket
} from 'src/apis/hr_ticket/purchaseRequestTicket.api';
import {
  approvalResignationTickets,
  cancelApprovalResignationTicket,
  cancelResignationTicket,
  hrConfirmResignationTicket,
  printResignationTicket
} from 'src/apis/hr_ticket/resignationTicket.api';
import {
  approvalTransportationTickets,
  cancelApprovalTransportationTickets,
  cancelTransportationTicket,
  confirmTransportationTicket,
  driverConfirmTransportationTicket,
  hrConfirmTransportationTicket,
  printTransportationTicket
} from 'src/apis/hr_ticket/transportationTicket.api';
import { getRoleByPermission } from 'src/apis/master_data/role.api';
import Close from 'src/assets/icons/close.png';
import Icon from 'src/components/v2/Icon';
import { ConfirmTypeEn } from 'src/constants/confirmTypeEn.enum';
import { DriverConfirmEn } from 'src/constants/driverConfirm.enum';
import { PermissionEn } from 'src/constants/permission.enum';
import { TransportationTypeEn } from 'src/constants/transportationType.enum';
import urlPath from 'src/constants/urlPath';
import { AppContext } from 'src/contexts/app.context';
import { HrTicketDetailContext } from 'src/contexts/hrTicketDetail.context';
import { TicketContext } from 'src/contexts/ticket.context';
import { useBasePath } from 'src/hooks/useBasePath';
import ConfirmTransportation from 'src/pages/v2/HrTicket/TransportationTicketDetail/ConfirmTransportation';
import EditTransportationTicket from 'src/pages/v2/HrTicket/TransportationTicketDetail/EditTransportationTicket';
import HrConfirmTransportation from 'src/pages/v2/HrTicket/TransportationTicketDetail/HrConfirmTransportation';
import AddApproveNote from 'src/pages/v2/HrTicket/components/AddApproveNote';
import AddRejectNote from 'src/pages/v2/HrTicket/components/AddRejectNote';
import AddRelatedTicket from 'src/pages/v2/HrTicket/components/AddRelatedTicket';
import AddReplacement from 'src/pages/v2/HrTicket/components/AddReplacement/AddReplacement';
import type { FlatItem } from 'src/types/elements.type';
import { ApprovalStatus, TicketStatus } from 'src/types/helpdesk/ticket.type';
import type {
  ITransportationTicket,
  ITransportationTicketDetail,
  TransportationTicketHrConfirm
} from 'src/types/hr_ticket/transportationTicket.type';
import type { IRoleUser } from 'src/types/master_data/roleUser.type';
import { showNotification } from 'src/utils/notification';
import BtnCustom from './BtnCustom';
import './btnHandleList.scss';

export type TicketResponses = {
  ticketId: number;
  step: number;
};

export type ApprovalRequestType = {
  ticketResponses: TicketResponses[];
  action: ApprovalStatus;
  note?: string;
  replacerId?: number;
};

export type ApprovalPackageRequestType = {
  id: number;
  ticketIdsNotApproved: number[];
  step: number;
  action: ApprovalStatus;
  note?: string;
};

export type HrConfirmRequestType = {
  id?: number;
  confirmType: ConfirmTypeEn;
  confirmDate?: Date;
  confirmNote?: string;
  resignDate?: Date;
};

export type TransportationConfirmRequestType = {
  ticketId: number;
  confirmType: ConfirmTypeEn;
  confirmNote?: string;
  vehicleId?: number;
  driverId?: number;
};

export type DriverConfirmType = {
  ticketId: number;
  confirmType: DriverConfirmEn;
};

export interface OpenReplacementType {
  isOpen?: boolean;
  ticketResponses: TicketResponses[];
  approvalStatus: ApprovalStatus;
}

export interface OpenConfirmTransportationType {
  isOpen?: boolean;
  vehicleType?: FlatItem;
  departureDate?: Date;
  transportationType?: FlatItem;
}

export interface OpenHrConfirmTransportationType {
  isOpen: boolean;
  distance?: number;
  transportationTicketDetails?: ITransportationTicketDetail[];
}

export type OpenEditTransportationType = {
  isOpen?: boolean;
  transportationTicket?: ITransportationTicket;
};

export interface OpenNoteType {
  isOpen?: boolean;
}

type Props = {
  type:
    | 'leaveTicket'
    | 'leavePackage'
    | 'advancePaymentTicket'
    | 'businessTripTicket'
    | 'overtimeTicket'
    | 'expenseTrackingRequestTicket'
    | 'transportationTicket'
    | 'handoverTicket'
    | 'equipmentSupplyRequestTicket'
    | 'additionalWorkingTimeTicket'
    | 'additionalWorkingTimePackage'
    | 'purchaseRequestTicket'
    | 'resignationTicket';
  onClick?: () => void;
};

export interface OpenRelatedTicketType {
  isOpen: boolean;
  businessTripTicketId: string;
  equipmentSupplyTicketId: string;
}

const BtnHrHandle = ({ type, onClick }: Props) => {
  const { ticketSelected, setTicketSelected, userInfo, qrCode, isUserAdmin } = useContext(AppContext);
  const { setInvalidKey } = useContext(TicketContext);
  const [, setUsersConfirmTransportation] = useState<IRoleUser[]>([]);
  const {
    leaveTicket,
    leavePackage,
    overtimeTicket,
    expenseTrackingRequestTicket,
    businessTripTicket,
    transportationTicket,
    advancePaymentTicket,
    purchaseRequestTicket,
    equipmentSupplyRequestTicket,
    additionalWorkingTimeTicket,
    resignationTicket
  } = useContext(HrTicketDetailContext);
  const [openReplacement, setOpenReplacement] = useState<OpenReplacementType>({
    isOpen: false,
    ticketResponses: [],
    approvalStatus: ApprovalStatus.Reject
  });

  const [openRejectNote, setOpenRejectNote] = useState<boolean>(false);
  const [openHrRejectNote, setOpenHrRejectNote] = useState<boolean>(false);
  const [openConfirmTransportation, setOpenConfirmTransportation] = useState<OpenConfirmTransportationType>({
    isOpen: false
  });
  const [openNoConfirmNoteTransportation, setOpenNoConfirmNoteTransportation] = useState<boolean>(false);
  const [openHrConfirmTransportation, setOpenHrConfirmTransportation] = useState<OpenHrConfirmTransportationType>({
    isOpen: false
  });
  const [openHrNoConfirmNoteTransportation, setOpenHrNoConfirmNoteTransportation] = useState<boolean>(false);
  const [openEditTransportation, setOpenEditTransportation] = useState<OpenEditTransportationType>({
    isOpen: false
  });
  const [openRelatedTicket, setOpenRelatedTicket] = useState<OpenRelatedTicketType>({
    businessTripTicketId: '',
    equipmentSupplyTicketId: '',
    isOpen: false
  });
  const [openApproveNote, setOpenApproveNote] = useState<boolean>(false);
  const [approveNoteData, setApproveNoteData] = useState<{
    ticketResponses: TicketResponses[];
    approvalStatus: ApprovalStatus;
    replacerId?: number;
  } | null>(null);

  const [openHrConfirmNote, setOpenHrConfirmNote] = useState<boolean>(false);
  const [hrConfirmNoteData, setHrConfirmNoteData] = useState<{
    confirmType: ConfirmTypeEn;
  } | null>(null);

  const basePath = useBasePath();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Get role confirm transportation ticket
  useQuery({
    queryKey: ['roleByPermissions'],
    queryFn: () => getRoleByPermission(PermissionEn.confirm_transportation),
    staleTime: 60 * 1000,
    enabled: basePath?.includes(urlPath.transportationTicket.location),
    onSuccess: (response) => {
      const roleUsers = response.data.map(({ roleUsers }) => [...roleUsers]);

      setUsersConfirmTransportation(roleUsers?.[0]);
    }
  });

  //#region cancel ticket
  const cancelLeaveTicketMutation = useMutation({
    mutationFn: (id: number) => cancelLeaveTicket(id),
    onSuccess: (_, id) => {
      showNotification(`Hủy phiếu thành công - ${leaveTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelResignationTicketMutation = useMutation({
    mutationFn: (id: number) => cancelResignationTicket(id),
    onSuccess: (_, id) => {
      showNotification(`Hủy phiếu thành công - ${resignationTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelOvertimeTicketMutation = useMutation({
    mutationFn: (id: number) => cancelOvertimeTicket(id),
    onSuccess: (_, id) => {
      showNotification(`Hủy phiếu thành công - ${overtimeTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelETRTicketMutation = useMutation({
    mutationFn: (id: number) => cancelTicket(id),
    onSuccess: (_, id) => {
      showNotification(`Hủy phiếu thành công - ${expenseTrackingRequestTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelBusinessTripTicketMutation = useMutation({
    mutationFn: (id: number) => cancelBusinessTripTicket(id),
    onSuccess: (_, id) => {
      showNotification(`Hủy phiếu thành công - ${businessTripTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelTransportationTicketMutation = useMutation({
    mutationFn: (id: number) => cancelTransportationTicket(id),
    onSuccess: (_, id) => {
      showNotification(`Hủy phiếu thành công - ${transportationTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelAdvancePaymentTicketMutation = useMutation({
    mutationFn: (id: number) => cancelAdvancePaymentTicket(id),
    onSuccess: (_, id) => {
      showNotification(`Hủy phiếu thành công - ${advancePaymentTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelPurchaseRequestTicketMutation = useMutation({
    mutationFn: (id: number) => cancelPurchaseRequestTicket(id),
    onSuccess: (_, id) => {
      showNotification(`Hủy phiếu thành công - ${purchaseRequestTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelEquipmentSupplyRequestTicketMutation = useMutation({
    mutationFn: (id: number) => cancelEquipmentSupplyRequestTicket(id),
    onSuccess: (_, id) => {
      showNotification(`Hủy phiếu thành công - ${equipmentSupplyRequestTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelAdditionalWorkingTimeTicketMutation = useMutation({
    mutationFn: (id: number) => cancelAdditionalWorkingTimeTicket(id),
    onSuccess: (_, id) => {
      showNotification(`Hủy phiếu thành công - ${additionalWorkingTimeTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });
  //#endregion

  //#region print ticket
  const printLeaveTicketMutation = useMutation({
    mutationFn: (id: number) => printLeaveTicket(id),
    onSuccess: (data) => {
      showNotification(`In phiếu thành công - ${leaveTicket?.data.code}`, 'success');
      const url = window.URL.createObjectURL(
        new Blob([data.data], {
          type: `application/pdf;charset=UTF-8`
        })
      );
      window.open(url, '_blank');
    }
  });

  const printOvertimeTicketMutation = useMutation({
    mutationFn: (id: number) => printOvertimeTicket(id),
    onSuccess: (data) => {
      showNotification(`In phiếu thành công - ${overtimeTicket?.data.code}`, 'success');
      const url = window.URL.createObjectURL(
        new Blob([data.data], {
          type: `application/pdf;charset=UTF-8`
        })
      );
      window.open(url, '_blank');
    }
  });

  const printETRTicketMutation = useMutation({
    mutationFn: (id: number) => printTicket(id),
    onSuccess: (data) => {
      showNotification(`In phiếu thành công - ${expenseTrackingRequestTicket?.data.code}`, 'success');
      const url = window.URL.createObjectURL(
        new Blob([data.data], {
          type: `application/pdf;charset=UTF-8`
        })
      );
      window.open(url, '_blank');
    }
  });

  const printBusinessTripTicketMutation = useMutation({
    mutationFn: async (id: number) => await printBusinessTripTicket(id),
    onSuccess: (data) => {
      showNotification(`In phiếu thành công - ${businessTripTicket?.data.code}`, 'success');
      const url = window.URL.createObjectURL(
        new Blob([data.data], {
          type: `application/pdf;charset=UTF-8`
        })
      );
      window.open(url, '_blank');
    }
  });

  const printTransportationTicketMutation = useMutation({
    mutationFn: (id: number) => printTransportationTicket(id),
    onSuccess: (data) => {
      showNotification(`In phiếu thành công - ${transportationTicket?.data.code}`, 'success');
      const url = window.URL.createObjectURL(
        new Blob([data.data], {
          type: `application/pdf;charset=UTF-8`
        })
      );
      window.open(url, '_blank');
    }
  });

  const printAdvancePaymentTicketMutation = useMutation({
    mutationFn: (id: number) => printAdvancePaymentTicket(id),
    onSuccess: (data) => {
      showNotification(`In phiếu thành công - ${advancePaymentTicket?.data.code}`, 'success');
      const url = window.URL.createObjectURL(
        new Blob([data.data], {
          type: `application/pdf;charset=UTF-8`
        })
      );
      window.open(url, '_blank');
    }
  });

  const printPurchaseRequestTicketMutation = useMutation({
    mutationFn: (id: number) => printPurchaseRequestTicket(id),
    onSuccess: (data) => {
      showNotification(`In phiếu thành công - ${purchaseRequestTicket?.data.code}`, 'success');
      const url = window.URL.createObjectURL(
        new Blob([data.data], {
          type: `application/pdf;charset=UTF-8`
        })
      );
      window.open(url, '_blank');
    }
  });

  const printEquipmentSupplyRequestTicketMutation = useMutation({
    mutationFn: (id: number) => printEquipmentSupplyRequestTicket(id),
    onSuccess: (data) => {
      showNotification(`In phiếu thành công - ${equipmentSupplyRequestTicket?.data.code}`, 'success');
      const url = window.URL.createObjectURL(
        new Blob([data.data], {
          type: `application/pdf;charset=UTF-8`
        })
      );
      window.open(url, '_blank');
    }
  });
  const printResignationTicketMutation = useMutation({
    mutationFn: (id: number) => printResignationTicket(id),
    onSuccess: (data) => {
      showNotification(`In phiếu thành công - ${resignationTicket?.data.code}`, 'success');
      const url = window.URL.createObjectURL(
        new Blob([data.data], {
          type: `application/pdf;charset=UTF-8`
        })
      );
      window.open(url, '_blank');
    }
  });
  //#endregion

  //#region duplicate ticket
  const duplicateOvertimeTicketMutation = useMutation({
    mutationFn: (id: number) => duplicateOvertimeTicket(id),
    onSuccess: (data) => {
      showNotification(`Sao chép phiếu thành công - ${overtimeTicket?.data.code}`, 'success');
      navigate(`${urlPath.overtimeTicketEdit.location}/${data.data}`);
    }
  });

  //#region transportation confirm ticket
  const confirmTransportationTicketMutation = useMutation({
    mutationFn: (rq: TransportationConfirmRequestType) =>
      confirmTransportationTicket(rq.ticketId, rq.confirmType, rq.confirmNote, rq.vehicleId, rq.driverId),
    onSuccess: (_, rq) => {
      if (rq.confirmType === ConfirmTypeEn.Confirmed) {
        showNotification(`Xác nhận thành công - ${transportationTicket?.data.code}`, 'success');
      } else if (rq.confirmType === ConfirmTypeEn.Reject) {
        showNotification(`Từ chối xác nhận thành công - ${transportationTicket?.data.code}`, 'success');
      }
      handleRefresh();
    }
  });

  //#region transportation hr confirm ticket
  const hrConfirmTransportationTicketMutation = useMutation({
    mutationFn: (rq: TransportationTicketHrConfirm) => hrConfirmTransportationTicket(transportationTicket?.data.id, rq),
    onSuccess: (_, rq) => {
      if (rq.confirmType === ConfirmTypeEn.Confirmed) {
        showNotification(`Xác nhận thành công - ${transportationTicket?.data.code}`, 'success');
      } else if (rq.confirmType === ConfirmTypeEn.Reject) {
        showNotification(`Từ chối xác nhận thành công - ${transportationTicket?.data.code}`, 'success');
      }
      handleRefresh();
    }
  });

  //#region driver confirm ticket
  const driverConfirmTransportationTicketMutation = useMutation({
    mutationFn: (rq: DriverConfirmType) => driverConfirmTransportationTicket(rq.ticketId, rq.confirmType),
    onSuccess: (_, rq) => {
      if (rq.confirmType === DriverConfirmEn.START) {
        showNotification(`Xác nhận bắt đầu thành công - ${transportationTicket?.data.code}`, 'success');
      } else if (rq.confirmType === DriverConfirmEn.END) {
        showNotification(`Xác nhận hoàn thành thành công - ${transportationTicket?.data.code}`, 'success');
      }
      handleRefresh();
    }
  });

  //#endregion

  //#region approval ticket
  const approvalLeaveTicketMutation = useMutation({
    mutationFn: (rq: ApprovalRequestType) =>
      approvalLeaveTickets(rq.ticketResponses, rq.action, rq.note || '', rq.replacerId),
    onSuccess: (_, rq) => {
      if (rq.action === ApprovalStatus.Approve) {
        showNotification(`Phê duyệt thành công - ${leaveTicket?.data.code}`, 'success');
      } else if (rq.action === ApprovalStatus.Reject) {
        showNotification(`Từ chối phiếu thành công - ${leaveTicket?.data.code}`, 'success');
      }
      handleRefresh();
    }
  });

  const approvalResignationTicketMutation = useMutation({
    mutationFn: (rq: ApprovalRequestType) =>
      approvalResignationTickets(rq.ticketResponses, rq.action, rq.note || '', rq.replacerId),
    onSuccess: (_, rq) => {
      if (rq.action === ApprovalStatus.Approve) {
        showNotification(`Phê duyệt thành công - ${resignationTicket?.data.code}`, 'success');
      } else if (rq.action === ApprovalStatus.Reject) {
        showNotification(`Từ chối phiếu thành công - ${resignationTicket?.data.code}`, 'success');
      }
      handleRefresh();
    }
  });

  const approvalLeavePackageMutation = useMutation({
    mutationFn: (rq: ApprovalRequestType) => approvalLeavePackage(rq.ticketResponses, rq.action, rq.note || ''),
    onSuccess: (_, rq) => {
      if (rq.action === ApprovalStatus.Approve) {
        showNotification(`Phê duyệt thành công `, 'success');
      } else if (rq.action === ApprovalStatus.Reject) {
        showNotification(`Từ chối phiếu thành công`, 'success');
      }
      handleRefresh();
    }
  });

  const approvalOvertimeTicketMutation = useMutation({
    mutationFn: (rq: ApprovalRequestType) => approvalOvertimeTickets(rq.ticketResponses, rq.action, rq.note),
    onSuccess: (_, rq) => {
      if (rq.action === ApprovalStatus.Approve) {
        showNotification(`Phê duyệt thành công - ${overtimeTicket?.data.code}`, 'success');
      } else if (rq.action === ApprovalStatus.Reject) {
        showNotification(`Từ chối phiếu thành công - ${overtimeTicket?.data.code}`, 'success');
      }
      handleRefresh();
    }
  });

  const approvalETRTicketMutation = useMutation({
    mutationFn: (rq: ApprovalRequestType) => approvalTickets(rq.ticketResponses, rq.action, rq.note),
    onSuccess: (_, rq) => {
      if (rq.action === ApprovalStatus.Approve) {
        showNotification(`Phê duyệt thành công - ${expenseTrackingRequestTicket?.data.code}`, 'success');
      } else if (rq.action === ApprovalStatus.Reject) {
        showNotification(`Từ chối phiếu thành công - ${expenseTrackingRequestTicket?.data.code}`, 'success');
      }
      handleRefresh();
    }
  });

  const approvalBusinessTripTicketMutation = useMutation({
    mutationFn: (rq: ApprovalRequestType) => approvalBusinessTripTickets(rq.ticketResponses, rq.action, rq.note),
    onSuccess: (_, rq) => {
      if (rq.action === ApprovalStatus.Approve) {
        showNotification(`Phê duyệt thành công - ${businessTripTicket?.data.code}`, 'success');
      } else if (rq.action === ApprovalStatus.Reject) {
        showNotification(`Từ chối phiếu thành công - ${businessTripTicket?.data.code}`, 'success');
      }
      handleRefresh();
    }
  });

  const approvalTransportationTicketMutation = useMutation({
    mutationFn: (rq: ApprovalRequestType) => approvalTransportationTickets(rq.ticketResponses, rq.action, rq.note),
    onSuccess: (_, rq) => {
      if (rq.action === ApprovalStatus.Approve) {
        showNotification(`Phê duyệt thành công - ${transportationTicket?.data.code}`, 'success');
      } else if (rq.action === ApprovalStatus.Reject) {
        showNotification(`Từ chối phiếu thành công - ${transportationTicket?.data.code}`, 'success');
      }
      handleRefresh();
    }
  });

  const approvalAdvancePaymentTicketMutation = useMutation({
    mutationFn: (rq: ApprovalRequestType) => approvalAdvancePaymentTickets(rq.ticketResponses, rq.action, rq.note),
    onSuccess: (_, rq) => {
      if (rq.action === ApprovalStatus.Approve) {
        showNotification(`Phê duyệt thành công - ${advancePaymentTicket?.data.code}`, 'success');
      } else if (rq.action === ApprovalStatus.Reject) {
        showNotification(`Từ chối phiếu thành công - ${advancePaymentTicket?.data.code}`, 'success');
      }
      handleRefresh();
    }
  });

  const approvalPurchaseRequestTicketMutation = useMutation({
    mutationFn: (rq: ApprovalRequestType) => approvalPurchaseRequestTickets(rq.ticketResponses, rq.action, rq.note),
    onSuccess: (_, rq) => {
      if (rq.action === ApprovalStatus.Approve) {
        showNotification(`Phê duyệt thành công - ${purchaseRequestTicket?.data.code}`, 'success');
      } else if (rq.action === ApprovalStatus.Reject) {
        showNotification(`Từ chối phiếu thành công - ${purchaseRequestTicket?.data.code}`, 'success');
      }
      handleRefresh();
    }
  });

  const approvalEquipmentSupplyRequestTicketMutation = useMutation({
    mutationFn: (rq: ApprovalRequestType) =>
      approvalEquipmentSupplyRequestTickets(rq.ticketResponses, rq.action, rq.note),
    onSuccess: (_, rq) => {
      if (rq.action === ApprovalStatus.Approve) {
        showNotification(`Phê duyệt thành công - ${equipmentSupplyRequestTicket?.data.code}`, 'success');
      } else if (rq.action === ApprovalStatus.Reject) {
        showNotification(`Từ chối phiếu thành công - ${equipmentSupplyRequestTicket?.data.code}`, 'success');
      }
      handleRefresh();
    }
  });

  const approvalAdditionalWorkingTimeTicketMutation = useMutation({
    mutationFn: (rq: ApprovalRequestType) =>
      approvalAdditionalWorkingTimeTickets(rq.ticketResponses, rq.action, rq.note),
    onSuccess: (_, rq) => {
      if (rq.action === ApprovalStatus.Approve) {
        showNotification(`Phê duyệt thành công - ${additionalWorkingTimeTicket?.data.code}`, 'success');
      } else if (rq.action === ApprovalStatus.Reject) {
        showNotification(`Từ chối phiếu thành công - ${additionalWorkingTimeTicket?.data.code}`, 'success');
      }
      handleRefresh();
    }
  });
  //#endregion

  //#region cancel approval
  const cancelApprovalLeaveTicketMutation = useMutation({
    mutationFn: (ticketResponses: TicketResponses) =>
      cancelApprovalLeaveTicket(String(ticketResponses.ticketId), ticketResponses.step),
    onSuccess: (data) => {
      showNotification(`Thu hồi phê duyệt thành công - ${leaveTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });
  const cancelApprovalResignationTicketMutation = useMutation({
    mutationFn: (ticketResponses: TicketResponses) =>
      cancelApprovalResignationTicket(String(ticketResponses.ticketId), ticketResponses.step),
    onSuccess: (data) => {
      showNotification(`Thu hồi phê duyệt thành công - ${resignationTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelApprovalLeavePackageMutation = useMutation({
    mutationFn: (ticketResponses: TicketResponses) =>
      cancelApprovalLeavePackage(String(ticketResponses.ticketId), ticketResponses.step),
    onSuccess: (data) => {
      showNotification(`Thu hồi phê duyệt thành công - ${leavePackage?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelApprovalOvertimeTicketMutation = useMutation({
    mutationFn: (ticketResponses: TicketResponses) =>
      cancelApprovalOvertimeTickets(String(ticketResponses.ticketId), ticketResponses.step),
    onSuccess: (data) => {
      showNotification(`Thu hồi phê duyệt thành công - ${overtimeTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelApprovalETRTicketMutation = useMutation({
    mutationFn: (ticketResponses: TicketResponses) =>
      cancelApprovalTickets(String(ticketResponses.ticketId), ticketResponses.step),
    onSuccess: (data) => {
      showNotification(`Thu hồi phê duyệt thành công - ${expenseTrackingRequestTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelApprovalBusinessTripTicketMutation = useMutation({
    mutationFn: (ticketResponses: TicketResponses) =>
      cancelApprovalBusinessTripTickets(String(ticketResponses.ticketId), ticketResponses.step),
    onSuccess: (data) => {
      showNotification(`Thu hồi phê duyệt thành công - ${businessTripTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelApprovalTransportationTicketMutation = useMutation({
    mutationFn: (ticketResponses: TicketResponses) =>
      cancelApprovalTransportationTickets(String(ticketResponses.ticketId), ticketResponses.step),
    onSuccess: (data) => {
      showNotification(`Thu hồi phê duyệt thành công - ${transportationTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelApprovalAdvancePaymentTicketMutation = useMutation({
    mutationFn: (ticketResponses: TicketResponses) =>
      cancelApprovalAdvancePaymentTickets(String(ticketResponses.ticketId), ticketResponses.step),
    onSuccess: (data) => {
      showNotification(`Thu hồi phê duyệt thành công - ${advancePaymentTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelApprovalPurchaseRequestTicketMutation = useMutation({
    mutationFn: (ticketResponses: TicketResponses) =>
      cancelApprovalPurchaseRequestTickets(String(ticketResponses.ticketId), ticketResponses.step),
    onSuccess: (data) => {
      showNotification(`Thu hồi phê duyệt thành công - ${purchaseRequestTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelApprovalEquipmentSupplyRequestTicketMutation = useMutation({
    mutationFn: (ticketResponses: TicketResponses) =>
      cancelApprovalEquipmentSupplyRequestTickets(String(ticketResponses.ticketId), ticketResponses.step),
    onSuccess: (data) => {
      showNotification(`Thu hồi phê duyệt thành công - ${equipmentSupplyRequestTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  const cancelApprovalAdditionalWorkingTimeTicketMutation = useMutation({
    mutationFn: (ticketResponses: TicketResponses) =>
      cancelApprovalAdditionalWorkingTimeTicket(String(ticketResponses.ticketId), ticketResponses.step),
    onSuccess: (data) => {
      showNotification(`Thu hồi phê duyệt thành công - ${additionalWorkingTimeTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });
  //#endregion

  //#region hr confirm
  const hrConfirmLeaveTicketMutation = useMutation({
    mutationFn: (rq: HrConfirmRequestType) => hrConfirmLeaveTicket(rq.id, rq.confirmType, [], rq?.confirmNote),
    onSuccess: (_, rq) => {
      if (rq.confirmType === ConfirmTypeEn.Reject) {
        showNotification(`Từ chối phiếu thành công - ${leaveTicket?.data.code}`, 'success');
        handleRefresh();
      }
    }
  });

  const hrConfirmResignationTicketMutation = useMutation({
    mutationFn: (rq: HrConfirmRequestType) =>
      hrConfirmResignationTicket(rq.id, rq.confirmType, rq.confirmDate ?? new Date(), rq.confirmNote, rq.resignDate),
    onSuccess: (_, rq) => {
      switch (rq.confirmType) {
        case ConfirmTypeEn.Reject:
          showNotification(`Từ chối phiếu thành công - ${resignationTicket?.data.code}`, 'success');
          break;
        case ConfirmTypeEn.Confirmed:
          showNotification(`Xác nhận phiếu thành công - ${resignationTicket?.data.code}`, 'success');
          break;
        default:
          break;
      }
      handleRefresh();
    }
  });

  const hrConfirmBusinessTripTicketMutation = useMutation({
    mutationFn: (rq: HrConfirmRequestType) =>
      hrConfirmBusinessTripTicket(rq.id, {
        confirmType: rq.confirmType,
        confirmNote: rq?.confirmNote || ''
      }),
    onSuccess: (_, rq) => {
      if (rq.confirmType === ConfirmTypeEn.Confirmed) {
        showNotification(`Xác nhận phiếu thành công - ${businessTripTicket?.data.code}`, 'success');
      } else if (rq.confirmType === ConfirmTypeEn.Reject) {
        showNotification(`Từ chối phiếu thành công - ${businessTripTicket?.data.code}`, 'success');
      }
      handleRefresh();
    }
  });

  const hrConfirmOvertimeTicketMutation = useMutation({
    mutationFn: (rq: HrConfirmRequestType) => hrConfirmOvertimeTicket(rq.id, rq.confirmType, rq?.confirmNote),
    onSuccess: (_, rq) => {
      if (rq.confirmType === ConfirmTypeEn.Confirmed) {
        showNotification(`Xác nhận phiếu thành công - ${overtimeTicket?.data.code}`, 'success');
      } else if (rq.confirmType === ConfirmTypeEn.Reject) {
        showNotification(`Từ chối phiếu thành công - ${overtimeTicket?.data.code}`, 'success');
      }
      handleRefresh();
    }
  });

  //#endregion

  const handleCancel = (id: number) => {
    switch (type) {
      case 'leaveTicket':
        cancelLeaveTicketMutation.mutate(id);
        break;
      case 'overtimeTicket':
        cancelOvertimeTicketMutation.mutate(id);
        break;
      case 'expenseTrackingRequestTicket':
        cancelETRTicketMutation.mutate(id);
        break;
      case 'businessTripTicket':
        cancelBusinessTripTicketMutation.mutate(id);
        break;
      case 'transportationTicket':
        cancelTransportationTicketMutation.mutate(id);
        break;
      case 'advancePaymentTicket':
        cancelAdvancePaymentTicketMutation.mutate(id);
        break;
      case 'purchaseRequestTicket':
        cancelPurchaseRequestTicketMutation.mutate(id);
        break;
      case 'equipmentSupplyRequestTicket':
        cancelEquipmentSupplyRequestTicketMutation.mutate(id);
        break;
      case 'additionalWorkingTimeTicket':
        cancelAdditionalWorkingTimeTicketMutation.mutate(id);
        break;
      case 'resignationTicket':
        cancelResignationTicketMutation.mutate(id);
        break;
      default:
        break;
    }
  };

  const handlePrint = (id: number) => {
    switch (type) {
      case 'leaveTicket':
        printLeaveTicketMutation.mutate(id);
        break;
      case 'overtimeTicket':
        printOvertimeTicketMutation.mutate(id);
        break;
      case 'expenseTrackingRequestTicket':
        printETRTicketMutation.mutate(id);
        break;
      case 'businessTripTicket':
        printBusinessTripTicketMutation.mutate(id);
        break;
      case 'transportationTicket':
        printTransportationTicketMutation.mutate(id);
        break;
      case 'advancePaymentTicket':
        printAdvancePaymentTicketMutation.mutate(id);
        break;
      case 'purchaseRequestTicket':
        printPurchaseRequestTicketMutation.mutate(id);
        break;
      case 'equipmentSupplyRequestTicket':
        printEquipmentSupplyRequestTicketMutation.mutate(id);
        break;
      case 'resignationTicket':
        printResignationTicketMutation.mutate(id);
        break;

      default:
        break;
    }
  };

  const handleDuplicate = (id: number) => {
    switch (type) {
      case 'overtimeTicket':
        duplicateOvertimeTicketMutation.mutate(id);
        break;

      default:
        break;
    }
  };

  const handleApproval = (ticketResponses: TicketResponses[], approvalStatus: ApprovalStatus) => {
    switch (type) {
      case 'leaveTicket':
        setOpenApproveNote(true);
        setApproveNoteData({ ticketResponses, approvalStatus, replacerId: leaveTicket?.data.replacerId });
        break;
      case 'overtimeTicket':
        setOpenApproveNote(true);
        setApproveNoteData({ ticketResponses, approvalStatus });
        // approvalOvertimeTicketMutation.mutate({ ticketResponses, action: approvalStatus });
        break;
      case 'businessTripTicket':
        setOpenApproveNote(true);
        setApproveNoteData({ ticketResponses, approvalStatus });
        // approvalBusinessTripTicketMutation.mutate({ ticketResponses, action: approvalStatus });
        break;
      case 'resignationTicket':
        setOpenApproveNote(true);
        setApproveNoteData({ ticketResponses, approvalStatus });
        break;
      case 'leavePackage':
        setOpenApproveNote(true);
        setApproveNoteData({ ticketResponses, approvalStatus });
        // approvalLeavePackageMutation.mutate({ ticketResponses, action: approvalStatus });
        break;
      case 'expenseTrackingRequestTicket':
        setOpenApproveNote(true);
        setApproveNoteData({ ticketResponses, approvalStatus });
        // approvalETRTicketMutation.mutate({ ticketResponses, action: approvalStatus });
        break;

      case 'transportationTicket':
        setOpenApproveNote(true);
        setApproveNoteData({ ticketResponses, approvalStatus });
        // approvalTransportationTicketMutation.mutate({ ticketResponses, action: approvalStatus });
        break;
      case 'advancePaymentTicket':
        setOpenApproveNote(true);
        setApproveNoteData({ ticketResponses, approvalStatus });
        // approvalAdvancePaymentTicketMutation.mutate({ ticketResponses, action: approvalStatus });
        break;
      case 'purchaseRequestTicket':
        setOpenApproveNote(true);
        setApproveNoteData({ ticketResponses, approvalStatus });
        // approvalPurchaseRequestTicketMutation.mutate({ ticketResponses, action: approvalStatus });
        break;
      case 'equipmentSupplyRequestTicket':
        setOpenApproveNote(true);
        setApproveNoteData({ ticketResponses, approvalStatus });
        // approvalEquipmentSupplyRequestTicketMutation.mutate({ ticketResponses, action: approvalStatus });
        break;
      case 'additionalWorkingTimeTicket':
        setOpenApproveNote(true);
        setApproveNoteData({ ticketResponses, approvalStatus });
        // approvalEquipmentSupplyRequestTicketMutation.mutate({ ticketResponses, action: approvalStatus });
        break;

      default:
        break;
    }
  };

  const handleCancelApproval = (id: number, step: number) => {
    switch (type) {
      case 'leaveTicket':
        cancelApprovalLeaveTicketMutation.mutate({ ticketId: id, step: step });
        break;
      case 'leavePackage':
        cancelApprovalLeavePackageMutation.mutate({ ticketId: id, step: step });
        break;
      case 'overtimeTicket':
        cancelApprovalOvertimeTicketMutation.mutate({ ticketId: id, step: step });
        break;
      case 'expenseTrackingRequestTicket':
        cancelApprovalETRTicketMutation.mutate({ ticketId: id, step: step });
        break;
      case 'businessTripTicket':
        cancelApprovalBusinessTripTicketMutation.mutate({ ticketId: id, step: step });
        break;
      case 'transportationTicket':
        cancelApprovalTransportationTicketMutation.mutate({ ticketId: id, step: step });
        break;
      case 'advancePaymentTicket':
        cancelApprovalAdvancePaymentTicketMutation.mutate({ ticketId: id, step: step });
        break;
      case 'purchaseRequestTicket':
        cancelApprovalPurchaseRequestTicketMutation.mutate({ ticketId: id, step: step });
        break;
      case 'equipmentSupplyRequestTicket':
        cancelApprovalEquipmentSupplyRequestTicketMutation.mutate({ ticketId: id, step: step });
        break;
      case 'additionalWorkingTimeTicket':
        cancelApprovalAdditionalWorkingTimeTicketMutation.mutate({ ticketId: id, step: step });
        break;
      case 'resignationTicket':
        cancelApprovalResignationTicketMutation.mutate({ ticketId: id, step: step });
        break;
      default:
        break;
    }
  };

  const handleConfirmTransportation = (note: string, vehicleId?: number, driverId?: number) => {
    confirmTransportationTicketMutation.mutate({
      ticketId: transportationTicket?.data.id as number,
      confirmType: ConfirmTypeEn.Confirmed,
      confirmNote: note,
      vehicleId: vehicleId,
      driverId: driverId
    });
  };

  const handleAddNoConfirmNoteTransportation = (note: string) => {
    confirmTransportationTicketMutation.mutate({
      ticketId: transportationTicket?.data.id as number,
      confirmType: ConfirmTypeEn.Reject,
      confirmNote: note
    });
  };

  const handleHrConfirmTransportation = (hrConfirmTransportation: TransportationTicketHrConfirm) => {
    const newHrConfirmTransportation = {
      ...hrConfirmTransportation,
      hrConfirms: hrConfirmTransportation?.hrConfirms?.map(({ id, employee, ...rest }) => ({ ...rest }))
    };
    hrConfirmTransportationTicketMutation.mutate(newHrConfirmTransportation);
  };

  const handleAddHrNoConfirmNoteTransportation = (note: string) => {
    hrConfirmTransportationTicketMutation.mutate({
      confirmType: ConfirmTypeEn.Reject,
      confirmNote: note
    });
  };

  const handleReject = () => {
    setOpenRejectNote(true);
  };

  const handleAddReplacement = (
    employeeId: number,
    ticketResponses: TicketResponses[],
    approvalStatus: ApprovalStatus
  ) => {
    approvalLeaveTicketMutation.mutate({ ticketResponses, action: approvalStatus, replacerId: employeeId });
  };

  const handleAddRejectNote = (note: string) => {
    switch (type) {
      case 'leaveTicket':
        approvalLeaveTicketMutation.mutate({
          ticketResponses: [
            { ticketId: leaveTicket?.data.id as number, step: leaveTicket?.data.waitingApprovalAt as number }
          ],
          action: ApprovalStatus.Reject,
          note: note
        });
        break;
      case 'leavePackage':
        approvalLeavePackageMutation.mutate({
          ticketResponses: [
            {
              ticketId: leavePackage?.data.id as number,
              step: leavePackage?.data.waitingApprovalAt as number
            }
          ],
          action: ApprovalStatus.Reject,
          note: note
        });
        break;
      case 'overtimeTicket':
        approvalOvertimeTicketMutation.mutate({
          ticketResponses: [
            { ticketId: overtimeTicket?.data.id as number, step: overtimeTicket?.data.waitingApprovalAt as number }
          ],
          action: ApprovalStatus.Reject,
          note: note
        });
        break;
      case 'expenseTrackingRequestTicket':
        approvalETRTicketMutation.mutate({
          ticketResponses: [
            {
              ticketId: expenseTrackingRequestTicket?.data.id as number,
              step: expenseTrackingRequestTicket?.data.waitingApprovalAt as number
            }
          ],
          action: ApprovalStatus.Reject,
          note: note
        });
        break;
      case 'businessTripTicket':
        approvalBusinessTripTicketMutation.mutate({
          ticketResponses: [
            {
              ticketId: businessTripTicket?.data.id as number,
              step: businessTripTicket?.data.waitingApprovalAt as number
            }
          ],
          action: ApprovalStatus.Reject,
          note: note
        });
        break;
      case 'transportationTicket':
        approvalTransportationTicketMutation.mutate({
          ticketResponses: [
            {
              ticketId: transportationTicket?.data.id as number,
              step: transportationTicket?.data.waitingApprovalAt as number
            }
          ],
          action: ApprovalStatus.Reject,
          note: note
        });
        break;
      case 'advancePaymentTicket':
        approvalAdvancePaymentTicketMutation.mutate({
          ticketResponses: [
            {
              ticketId: advancePaymentTicket?.data.id as number,
              step: advancePaymentTicket?.data.waitingApprovalAt as number
            }
          ],
          action: ApprovalStatus.Reject,
          note: note
        });
        break;
      case 'purchaseRequestTicket':
        approvalPurchaseRequestTicketMutation.mutate({
          ticketResponses: [
            {
              ticketId: purchaseRequestTicket?.data.id as number,
              step: purchaseRequestTicket?.data.waitingApprovalAt as number
            }
          ],
          action: ApprovalStatus.Reject,
          note: note
        });
        break;
      case 'equipmentSupplyRequestTicket':
        approvalEquipmentSupplyRequestTicketMutation.mutate({
          ticketResponses: [
            {
              ticketId: equipmentSupplyRequestTicket?.data.id as number,
              step: equipmentSupplyRequestTicket?.data.waitingApprovalAt as number
            }
          ],
          action: ApprovalStatus.Reject,
          note: note
        });
        break;
      case 'additionalWorkingTimePackage':
        approvalAdditionalWorkingTimeTicketMutation.mutate({
          ticketResponses: [
            {
              ticketId: advancePaymentTicket?.data.id as number,
              step: advancePaymentTicket?.data.waitingApprovalAt as number
            }
          ],
          action: ApprovalStatus.Reject,
          note: note
        });
        break;
      case 'resignationTicket':
        approvalResignationTicketMutation.mutate({
          ticketResponses: [
            {
              ticketId: resignationTicket?.data.id as number,
              step: resignationTicket?.data.waitingApprovalAt as number
            }
          ],
          action: ApprovalStatus.Reject,
          note: note
        });
        break;
      default:
        break;
    }
  };
  const handleAddApproveNote = (note: string, resignDate?: Date, replacerId?: number) => {
    if (!approveNoteData) return;

    switch (type) {
      case 'leaveTicket':
        return approvalLeaveTicketMutation.mutate({
          ticketResponses: approveNoteData.ticketResponses,
          action: approveNoteData.approvalStatus,
          note,
          replacerId
        });

      case 'overtimeTicket':
        return approvalOvertimeTicketMutation.mutate({
          ticketResponses: approveNoteData.ticketResponses,
          action: approveNoteData.approvalStatus,
          note: note
        });

      case 'businessTripTicket':
        return approvalBusinessTripTicketMutation.mutate({
          ticketResponses: approveNoteData.ticketResponses,
          action: approveNoteData.approvalStatus,
          note: note
        });
      case 'resignationTicket':
        return approvalResignationTicketMutation.mutate({
          ticketResponses: approveNoteData.ticketResponses,
          action: approveNoteData.approvalStatus,
          note: note
        });
      case 'leavePackage':
        return approvalLeavePackageMutation.mutate({
          ticketResponses: approveNoteData.ticketResponses,
          action: approveNoteData.approvalStatus,
          note: note
        });
      case 'expenseTrackingRequestTicket':
        return approvalETRTicketMutation.mutate({
          ticketResponses: approveNoteData.ticketResponses,
          action: approveNoteData.approvalStatus,
          note: note
        });

      case 'transportationTicket':
        return approvalTransportationTicketMutation.mutate({
          ticketResponses: approveNoteData.ticketResponses,
          action: approveNoteData.approvalStatus,
          note: note
        });

      case 'advancePaymentTicket':
        return approvalAdvancePaymentTicketMutation.mutate({
          ticketResponses: approveNoteData.ticketResponses,
          action: approveNoteData.approvalStatus,
          note: note
        });
      case 'purchaseRequestTicket':
        return approvalPurchaseRequestTicketMutation.mutate({
          ticketResponses: approveNoteData.ticketResponses,
          action: approveNoteData.approvalStatus,
          note: note
        });
      case 'equipmentSupplyRequestTicket':
        return approvalEquipmentSupplyRequestTicketMutation.mutate({
          ticketResponses: approveNoteData.ticketResponses,
          action: approveNoteData.approvalStatus,
          note: note
        });
      default:
        break;
    }

    setOpenApproveNote(false);
    setApproveNoteData(null);
  };

  const handleHrConfirm = (id: number, confirmType: ConfirmTypeEn) => {
    switch (type) {
      case 'overtimeTicket':
        hrConfirmOvertimeTicketMutation.mutate({ id, confirmType });
        break;
      case 'resignationTicket':
        setOpenHrConfirmNote(true);
        setHrConfirmNoteData({ confirmType });
        break;

      default:
        break;
    }
  };

  const handleHrReject = () => {
    setOpenHrRejectNote(true);
  };

  const handleAddHrRejectNote = (note: string) => {
    switch (type) {
      case 'leaveTicket':
        hrConfirmLeaveTicketMutation.mutate({
          id: leaveTicket?.data.id,
          confirmType: ConfirmTypeEn.Reject,
          confirmNote: note
        });
        break;
      case 'resignationTicket':
        hrConfirmResignationTicketMutation.mutate({
          id: resignationTicket?.data.id,
          confirmType: ConfirmTypeEn.Reject,
          confirmNote: note
        });
        break;
      case 'businessTripTicket':
        hrConfirmBusinessTripTicketMutation.mutate({
          id: businessTripTicket?.data.id,
          confirmType: ConfirmTypeEn.Reject,
          confirmNote: note
        });
        break;
      case 'overtimeTicket':
        hrConfirmOvertimeTicketMutation.mutate({
          id: overtimeTicket?.data.id,
          confirmType: ConfirmTypeEn.Reject,
          confirmNote: note
        });
        break;
      default:
        break;
    }
  };

  const handleAddHrConfirmNote = (note?: string, resignDate?: Date) => {
    if (!hrConfirmNoteData) return;

    hrConfirmResignationTicketMutation.mutate({
      id: resignationTicket?.data.id,
      confirmType: ConfirmTypeEn.Confirmed,
      confirmNote: note,
      resignDate: resignDate ?? resignationTicket?.data?.resignDate
    });
    setOpenHrConfirmNote(false);
    setHrConfirmNoteData(null);
  };

  const handleDriverConfirm = (id: number, confirmType: DriverConfirmEn) => {
    driverConfirmTransportationTicketMutation.mutate({ ticketId: id, confirmType });
  };

  const handleRefresh = () => {
    switch (type) {
      case 'leaveTicket':
        queryClient.invalidateQueries(['leaveTicket']);
        break;
      case 'leavePackage':
        queryClient.invalidateQueries(['leavePackage']);
        break;
      case 'overtimeTicket':
        queryClient.invalidateQueries(['overtimeTicket']);
        break;
      case 'businessTripTicket':
        queryClient.invalidateQueries(['businessTripTicket']);
        break;
      case 'transportationTicket':
        queryClient.invalidateQueries(['transportationTicket']);
        break;
      case 'advancePaymentTicket':
        queryClient.invalidateQueries(['advancePaymentTicket']);
        break;
      case 'purchaseRequestTicket':
        queryClient.invalidateQueries(['purchaseRequestTicket']);
        break;
      case 'equipmentSupplyRequestTicket':
        queryClient.invalidateQueries(['equipmentSupplyRequestTicket']);
        break;
      case 'additionalWorkingTimePackage':
        queryClient.invalidateQueries(['additionalWorkingTimePackage']);
        break;
      case 'additionalWorkingTimeTicket':
        queryClient.invalidateQueries(['additionalWorkingTimeTicket']);
        break;
      case 'resignationTicket':
        queryClient.invalidateQueries({
          queryKey: ['resignationTicket'],
          exact: false
        });
        break;
      default:
        break;
    }

    queryClient.invalidateQueries(['statusCount']);
    queryClient.invalidateQueries(['segmentedStatusCount']);

    // handleCloseDetail();
  };

  const handleCloseDetail = () => {
    // setIsTicketDetailCollapsed(true);
    setTicketSelected(undefined);
    setInvalidKey('');
    navigate(basePath ?? '');
  };

  const renderHrConfirmButtons = () => {
    if (
      basePath === urlPath.hrConfirmLeaveTicket.location &&
      leaveTicket?.data.hrConfirmedType === ConfirmTypeEn.WaitingConfirm &&
      leaveTicket.data.ticketStatus.id === TicketStatus.ApprovedRequest
    ) {
      return (
        <Flex gap={10}>
          <Popconfirm title={'Bạn chắc chắn xác nhận?'} onConfirm={onClick}>
            <BtnCustom
              type='confirm'
              size='small'
              isLoading={
                hrConfirmLeaveTicketMutation.variables?.confirmType === ConfirmTypeEn.Confirmed &&
                hrConfirmLeaveTicketMutation.isLoading
              }
            />
          </Popconfirm>
          <BtnCustom
            type='reject'
            size='small'
            onClick={handleHrReject}
            isLoading={
              hrConfirmLeaveTicketMutation.variables?.confirmType === ConfirmTypeEn.Reject &&
              hrConfirmLeaveTicketMutation.isLoading
            }
          />
        </Flex>
      );
    }
    if (
      basePath === urlPath.hrConfirmResignationTicket.location &&
      resignationTicket?.data.hrConfirmedType === ConfirmTypeEn.WaitingConfirm &&
      (resignationTicket.data.ticketStatus.id === TicketStatus.New ||
        resignationTicket.data.ticketStatus.id === TicketStatus.ApprovedRequest)
    ) {
      return (
        <Flex gap={10}>
          <Popconfirm
            title={'Bạn chắc chắn xác nhận?'}
            onConfirm={() => handleHrConfirm(resignationTicket?.data?.id, ConfirmTypeEn.Confirmed)}
          >
            <BtnCustom
              type='confirm'
              size='small'
              isLoading={
                hrConfirmResignationTicketMutation.variables?.confirmType === ConfirmTypeEn.Confirmed &&
                hrConfirmResignationTicketMutation.isLoading
              }
            />
          </Popconfirm>
        </Flex>
      );
    }
    if (
      basePath === urlPath.hrConfirmBusinessTripTicket.location &&
      businessTripTicket?.data.hrConfirmedType === ConfirmTypeEn.WaitingConfirm &&
      businessTripTicket?.data.ticketStatus.id === TicketStatus.ApprovedRequest
    ) {
      return (
        <Flex gap={10}>
          <BtnCustom
            type='confirm'
            size='small'
            onClick={onClick}
            isLoading={
              hrConfirmBusinessTripTicketMutation.variables?.confirmType === ConfirmTypeEn.Confirmed &&
              hrConfirmBusinessTripTicketMutation.isLoading
            }
          />
          <BtnCustom
            type='reject'
            size='small'
            onClick={handleHrReject}
            isLoading={
              hrConfirmBusinessTripTicketMutation.variables?.confirmType === ConfirmTypeEn.Reject &&
              hrConfirmBusinessTripTicketMutation.isLoading
            }
          />
        </Flex>
      );
    }
    if (
      basePath === urlPath.hrConfirmOvertimeTicket.location &&
      overtimeTicket?.data.hrConfirmedType === ConfirmTypeEn.WaitingConfirm &&
      overtimeTicket.data.ticketStatus.id === TicketStatus.ApprovedRequest &&
      overtimeTicket?.data?.isSupervisorConfirmed
    ) {
      return (
        <Flex gap={10}>
          <Popconfirm
            title={'Bạn chắc chắn xác nhận?'}
            onConfirm={() => handleHrConfirm(overtimeTicket?.data?.id, ConfirmTypeEn.Confirmed)}
          >
            <BtnCustom
              type='confirm'
              size='small'
              isLoading={
                hrConfirmOvertimeTicketMutation.variables?.confirmType === ConfirmTypeEn.Confirmed &&
                hrConfirmOvertimeTicketMutation.isLoading
              }
            />
          </Popconfirm>
          <BtnCustom
            type='reject'
            size='small'
            onClick={handleHrReject}
            isLoading={
              hrConfirmOvertimeTicketMutation.variables?.confirmType === ConfirmTypeEn.Reject &&
              hrConfirmOvertimeTicketMutation.isLoading
            }
          />
        </Flex>
      );
    }
  };

  const renderLeaveTicketButtons = () => {
    if (basePath === urlPath.leavePackageApproval.location && leavePackage?.data.ticketStatusId === TicketStatus.New) {
      return (
        <Flex gap={10}>
          {leavePackage.data.waitingApprovalAt && (
            <Fragment>
              <BtnCustom
                type='approve'
                onClick={() =>
                  handleApproval(
                    [{ ticketId: leavePackage.data.id, step: leavePackage.data.waitingApprovalAt }],
                    ApprovalStatus.Approve
                  )
                }
                isLoading={
                  approvalLeavePackageMutation.variables?.action === ApprovalStatus.Approve &&
                  approvalLeavePackageMutation.isLoading
                }
              />
              <BtnCustom
                type='reject'
                onClick={handleReject}
                isLoading={
                  approvalLeavePackageMutation.variables?.action === ApprovalStatus.Reject &&
                  approvalLeavePackageMutation.isLoading
                }
              />
            </Fragment>
          )}

          {leavePackage.data.approvals?.sort((x, y) => x.step - y.step)[leavePackage.data.approvals.length - 1]
            ?.approverId === userInfo?.id && (
            <Popconfirm
              title={'Bạn chắc chắn thu hồi phê duyệt?'}
              onConfirm={() =>
                handleCancelApproval(
                  leavePackage.data.id,
                  leavePackage.data.approvals?.sort((x, y) => x.step - y.step)[leavePackage.data.approvals.length - 1]
                    ?.step
                )
              }
            >
              <BtnCustom
                type='cancelApproval'
                stepCancelApproval={
                  leavePackage.data.approvals?.sort((x, y) => x.step - y.step)[leavePackage.data.approvals.length - 1]
                    ?.step
                }
                isLoading={cancelApprovalLeavePackageMutation.isLoading}
              />
            </Popconfirm>
          )}
        </Flex>
      );
    }

    if (basePath === urlPath.myLeaveTicket.location) {
      return (
        <Flex gap={10}>
          {(leaveTicket?.data.createdById === userInfo?.id || isUserAdmin) && leaveTicket?.data.isAvailableEdit && (
            <BtnCustom type='edit' onClick={() => navigate(`${urlPath.leaveTicketEdit.location}/${ticketSelected}`)} />
          )}
          {(leaveTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            leaveTicket?.data.ticketStatusId === TicketStatus.New && (
              <Popconfirm title={'Bạn chắc chắn hủy phiếu?'} onConfirm={() => handleCancel(leaveTicket.data?.id)}>
                <BtnCustom type='cancelTicket' isLoading={cancelLeaveTicketMutation.isLoading} />
              </Popconfirm>
            )}
          {(leaveTicket?.data.createdById === userInfo?.id ||
            leaveTicket?.data.leaveTicketDetails[0].employeeId === userInfo?.id ||
            leaveTicket?.data.leaveTicketDetails[0].employeeId === Number(qrCode?.substring(0, 4) || isUserAdmin)) &&
            (leaveTicket?.data.ticketStatusId === TicketStatus.New ||
              leaveTicket?.data.ticketStatusId === TicketStatus.ApprovedRequest) &&
            leaveTicket?.data.isGeneratedReport && (
              <BtnCustom
                type='print'
                onClick={() => handlePrint(leaveTicket.data?.id)}
                isLoading={printLeaveTicketMutation.isLoading}
              />
            )}
        </Flex>
      );
    }

    if (basePath === urlPath.leaveTicketApproval.location && leaveTicket?.data.ticketStatusId === TicketStatus.New) {
      return (
        <Flex gap={10}>
          {leaveTicket.data.waitingApprovalAt && (
            <>
              <BtnCustom
                type='approve'
                onClick={() =>
                  handleApproval(
                    [{ ticketId: leaveTicket.data.id, step: leaveTicket.data.waitingApprovalAt }],
                    ApprovalStatus.Approve
                  )
                }
                isLoading={
                  approvalLeaveTicketMutation.variables?.action === ApprovalStatus.Approve &&
                  approvalLeaveTicketMutation.isLoading
                }
              />

              <BtnCustom
                type='reject'
                onClick={handleReject}
                isLoading={
                  approvalLeaveTicketMutation.variables?.action === ApprovalStatus.Reject &&
                  approvalLeaveTicketMutation.isLoading
                }
              />
            </>
          )}

          {leaveTicket.data.approvals?.sort((x, y) => x.step - y.step)[leaveTicket.data.approvals.length - 1]
            ?.approverId === userInfo?.id && (
            <Popconfirm
              title={'Bạn chắc chắn thu hồi phê duyệt?'}
              onConfirm={() =>
                handleCancelApproval(
                  leaveTicket.data.id,
                  leaveTicket.data.approvals?.sort((x, y) => x.step - y.step)[leaveTicket.data.approvals.length - 1]
                    ?.step
                )
              }
            >
              <BtnCustom
                type='cancelApproval'
                stepCancelApproval={
                  leaveTicket.data.approvals?.sort((x, y) => x.step - y.step)[leaveTicket.data.approvals.length - 1]
                    ?.step
                }
                isLoading={cancelApprovalLeaveTicketMutation.isLoading}
              />
            </Popconfirm>
          )}
        </Flex>
      );
    }

    if (basePath === urlPath.myLeaveTicketSub.location) {
      return (
        <Flex gap={10}>
          {leaveTicket?.data.leaveTicketDetails[0].employeeId === Number(qrCode?.substring(0, 4)) &&
            leaveTicket?.data.isAvailableEdit && (
              <BtnCustom
                type='edit'
                onClick={() => {
                  handleCloseDetail();
                  navigate(`${urlPath.leaveTicketEditSub.location}/${ticketSelected}`);
                }}
              />
            )}
          {leaveTicket?.data.leaveTicketDetails[0].employeeId === Number(qrCode?.substring(0, 4)) &&
            leaveTicket?.data.ticketStatusId === TicketStatus.New && (
              <Popconfirm title={'Bạn chắc chắn hủy phiếu?'} onConfirm={() => handleCancel(leaveTicket.data?.id)}>
                <BtnCustom type='cancelTicket' isLoading={cancelLeaveTicketMutation.isLoading} />
              </Popconfirm>
            )}
          {leaveTicket?.data.leaveTicketDetails[0].employeeId === Number(qrCode?.substring(0, 4)) &&
            leaveTicket?.data.ticketStatusId === TicketStatus.New &&
            leaveTicket?.data.isGeneratedReport && (
              <BtnCustom
                type='print'
                onClick={() => handlePrint(leaveTicket.data?.id)}
                isLoading={printLeaveTicketMutation.isLoading}
              />
            )}
        </Flex>
      );
    }
  };

  const renderResignationTicketButtons = () => {
    if (basePath === urlPath.myResignationTicket.location) {
      return (
        <Flex gap={10}>
          {(resignationTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            resignationTicket?.data.isAvailableEdit && (
              <BtnCustom
                type='edit'
                onClick={() => navigate(`${urlPath.resignationTicketEdit.location}/${ticketSelected}`)}
              />
            )}
          {(resignationTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            resignationTicket?.data.ticketStatusId === TicketStatus.New && (
              <Popconfirm title={'Bạn chắc chắn hủy phiếu?'} onConfirm={() => handleCancel(resignationTicket.data?.id)}>
                <BtnCustom type='cancelTicket' isLoading={cancelResignationTicketMutation.isLoading} />
              </Popconfirm>
            )}
          {(resignationTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            (resignationTicket?.data.ticketStatusId === TicketStatus.New ||
              resignationTicket?.data.ticketStatusId === TicketStatus.ApprovedRequest) &&
            resignationTicket?.data.isGeneratedReport && (
              <BtnCustom
                type='print'
                onClick={() => handlePrint(resignationTicket.data?.id)}
                isLoading={printResignationTicketMutation.isLoading}
              />
            )}
        </Flex>
      );
    }

    if (
      basePath === urlPath.resignationTicketApproval.location &&
      resignationTicket?.data.ticketStatusId === TicketStatus.New
    ) {
      return (
        <Flex gap={10}>
          {resignationTicket.data.waitingApprovalAt && (
            <>
              <Popconfirm
                title={'Bạn chắc chắn phê duyệt?'}
                onConfirm={() =>
                  handleApproval(
                    [{ ticketId: resignationTicket.data.id, step: resignationTicket.data.waitingApprovalAt }],
                    ApprovalStatus.Approve
                  )
                }
              >
                <BtnCustom
                  type='approve'
                  isLoading={
                    approvalResignationTicketMutation.variables?.action === ApprovalStatus.Approve &&
                    approvalResignationTicketMutation.isLoading
                  }
                />
              </Popconfirm>
            </>
          )}

          {resignationTicket.data.approvals?.sort((x, y) => x.step - y.step)[
            resignationTicket.data.approvals.length - 1
          ]?.approverId === userInfo?.id && (
            <Popconfirm
              title={'Bạn chắc chắn thu hồi phê duyệt?'}
              onConfirm={() =>
                handleCancelApproval(
                  resignationTicket.data.id,
                  resignationTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    resignationTicket.data.approvals.length - 1
                  ]?.step
                )
              }
            >
              <BtnCustom
                type='cancelApproval'
                stepCancelApproval={
                  resignationTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    resignationTicket.data.approvals.length - 1
                  ]?.step
                }
                isLoading={cancelApprovalLeaveTicketMutation.isLoading}
              />
            </Popconfirm>
          )}
        </Flex>
      );
    }

    if (basePath === urlPath.myResignationTicketSub.location) {
      return (
        <Flex gap={10}>
          {resignationTicket?.data.createdById === Number(qrCode?.substring(0, 4)) &&
            resignationTicket?.data.isAvailableEdit && (
              <BtnCustom
                type='edit'
                onClick={() => {
                  handleCloseDetail();
                  navigate(`${urlPath.resignationTicketEditSub.location}/${ticketSelected}`);
                }}
              />
            )}
          {resignationTicket?.data.createdById === Number(qrCode?.substring(0, 4)) &&
            resignationTicket?.data.ticketStatusId === TicketStatus.New && (
              <Popconfirm title={'Bạn chắc chắn hủy phiếu?'} onConfirm={() => handleCancel(resignationTicket.data?.id)}>
                <BtnCustom type='cancelTicket' isLoading={cancelResignationTicketMutation.isLoading} />
              </Popconfirm>
            )}
          {resignationTicket?.data.createdById === Number(qrCode?.substring(0, 4)) &&
            resignationTicket?.data.ticketStatusId === TicketStatus.New &&
            resignationTicket?.data.isGeneratedReport && (
              <BtnCustom
                type='print'
                onClick={() => handlePrint(resignationTicket.data?.id)}
                isLoading={printResignationTicketMutation.isLoading}
              />
            )}
        </Flex>
      );
    }
  };
  const renderTransportationTicketButtons = () => {
    if (basePath === urlPath.myTransportationTicket.location) {
      return (
        <Flex gap={10}>
          {(transportationTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            transportationTicket?.data.isAvailableEdit && (
              <BtnCustom
                type='edit'
                onClick={() => navigate(`${urlPath.transportationTicketEdit.location}/${ticketSelected}`)}
              />
            )}
          {(transportationTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            transportationTicket?.data.ticketStatusId === TicketStatus.New && (
              <Popconfirm
                title={'Bạn chắc chắn hủy phiếu?'}
                onConfirm={() => handleCancel(transportationTicket.data?.id)}
              >
                <BtnCustom type='cancelTicket' isLoading={cancelTransportationTicketMutation.isLoading} />
              </Popconfirm>
            )}
          {(transportationTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            (transportationTicket?.data.ticketStatusId === TicketStatus.New ||
              transportationTicket?.data.ticketStatusId === TicketStatus.ApprovedRequest) &&
            transportationTicket?.data.isGeneratedReport && (
              <BtnCustom
                type='print'
                onClick={() => handlePrint(transportationTicket.data?.id)}
                isLoading={printTransportationTicketMutation.isLoading}
              />
            )}
        </Flex>
      );
    }

    if (
      basePath === urlPath.transportationTicketApproval.location &&
      transportationTicket?.data.ticketStatusId === TicketStatus.New
    ) {
      return (
        <Flex gap={10}>
          {transportationTicket.data.waitingApprovalAt && (
            <>
              <Popconfirm
                title={'Bạn chắc chắn phê duyệt?'}
                onConfirm={() =>
                  handleApproval(
                    [{ ticketId: transportationTicket.data.id, step: transportationTicket.data.waitingApprovalAt }],
                    ApprovalStatus.Approve
                  )
                }
              >
                <BtnCustom
                  type='approve'
                  isLoading={
                    approvalTransportationTicketMutation.variables?.action === ApprovalStatus.Approve &&
                    approvalTransportationTicketMutation.isLoading
                  }
                />
              </Popconfirm>

              <BtnCustom
                type='reject'
                onClick={handleReject}
                isLoading={
                  approvalTransportationTicketMutation.variables?.action === ApprovalStatus.Reject &&
                  approvalTransportationTicketMutation.isLoading
                }
              />
            </>
          )}

          {transportationTicket.data.approvals?.sort((x, y) => x.step - y.step)[
            transportationTicket.data.approvals.length - 1
          ]?.approverId === userInfo?.id && (
            <Popconfirm
              title={'Bạn chắc chắn thu hồi phê duyệt?'}
              onConfirm={() =>
                handleCancelApproval(
                  transportationTicket.data.id,
                  transportationTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    transportationTicket.data.approvals.length - 1
                  ]?.step
                )
              }
            >
              <BtnCustom
                type='cancelApproval'
                stepCancelApproval={
                  transportationTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    transportationTicket.data.approvals.length - 1
                  ]?.step
                }
                isLoading={cancelApprovalTransportationTicketMutation.isLoading}
              />
            </Popconfirm>
          )}
        </Flex>
      );
    }

    if (basePath === urlPath.transportationTicketConfirm.location) {
      if (transportationTicket?.data.confirmedType === ConfirmTypeEn.WaitingConfirm) {
        return (
          <Flex gap={10}>
            <BtnCustom
              type='confirm'
              onClick={() =>
                setOpenConfirmTransportation({
                  isOpen: true,
                  vehicleType: transportationTicket.data.vehicleType,
                  transportationType: transportationTicket.data.transportationType,
                  departureDate: transportationTicket.data.departureDate
                })
              }
              isLoading={
                confirmTransportationTicketMutation.variables?.confirmType === ConfirmTypeEn.Confirmed &&
                confirmTransportationTicketMutation.isLoading
              }
            />
            <BtnCustom
              type='reject'
              onClick={() => setOpenNoConfirmNoteTransportation(true)}
              isLoading={
                confirmTransportationTicketMutation.variables?.confirmType === ConfirmTypeEn.Reject &&
                confirmTransportationTicketMutation.isLoading
              }
            />
          </Flex>
        );
      } else if (
        transportationTicket?.data.confirmedType === ConfirmTypeEn.Confirmed &&
        transportationTicket?.data.transportationTypeId === TransportationTypeEn.shuttle &&
        userInfo?.id === transportationTicket?.data.confirmedById &&
        !transportationTicket?.data.endTime
      ) {
        return (
          <BtnCustom
            type='edit'
            content='Cập nhật thông tin'
            onClick={() => setOpenEditTransportation({ isOpen: true, transportationTicket: transportationTicket.data })}
          />
        );
      }
    }

    if (
      basePath === urlPath.hrConfirmTransportationTicket.location &&
      transportationTicket?.data.confirmedType === ConfirmTypeEn.WaitingConfirm
    ) {
      return (
        <Flex gap={10}>
          <BtnCustom
            type='confirm'
            onClick={() =>
              setOpenHrConfirmTransportation({
                isOpen: true,
                distance: transportationTicket.data.totalKm,
                transportationTicketDetails: transportationTicket.data.transportationTicketDetails
              })
            }
            isLoading={
              hrConfirmTransportationTicketMutation.variables?.confirmType === ConfirmTypeEn.Confirmed &&
              hrConfirmTransportationTicketMutation.isLoading
            }
          />
          <BtnCustom
            type='reject'
            onClick={() => setOpenHrNoConfirmNoteTransportation(true)}
            isLoading={
              hrConfirmTransportationTicketMutation.variables?.confirmType === ConfirmTypeEn.Reject &&
              hrConfirmTransportationTicketMutation.isLoading
            }
          />
        </Flex>
      );
    }

    if (
      basePath === urlPath.transportationTicketHandle.location &&
      transportationTicket?.data.confirmedType === ConfirmTypeEn.Confirmed &&
      transportationTicket?.data.transportationTypeId === TransportationTypeEn.shuttle
    ) {
      if (!transportationTicket?.data.startTime && !transportationTicket?.data.endTime) {
        return (
          <Flex gap={10}>
            <Popconfirm
              title='Bạn chắc chắn xác nhận khỏi hành?'
              onConfirm={() => handleDriverConfirm(transportationTicket.data.id, DriverConfirmEn.START)}
            >
              <BtnCustom
                type='driverConfirmStart'
                isLoading={
                  driverConfirmTransportationTicketMutation.variables?.confirmType === DriverConfirmEn.START &&
                  driverConfirmTransportationTicketMutation.isLoading
                }
              />
            </Popconfirm>
          </Flex>
        );
      } else if (transportationTicket?.data.startTime && !transportationTicket?.data.endTime) {
        return (
          <Flex gap={10}>
            <Popconfirm
              title='Bạn chắc chắn xác nhận hoàn thành?'
              onConfirm={() => handleDriverConfirm(transportationTicket.data.id, DriverConfirmEn.END)}
            >
              <BtnCustom
                type='driverConfirmEnd'
                isLoading={
                  driverConfirmTransportationTicketMutation.variables?.confirmType === DriverConfirmEn.END &&
                  driverConfirmTransportationTicketMutation.isLoading
                }
              />
            </Popconfirm>
          </Flex>
        );
      }
    }
  };

  const renderAdvancePaymentTicketButtons = () => {
    if (basePath === urlPath.myAdvancePaymentTicket.location) {
      return (
        <Flex gap={10}>
          {(advancePaymentTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            advancePaymentTicket?.data.isAvailableEdit && (
              <BtnCustom
                type='edit'
                onClick={() => navigate(`${urlPath.advancePaymentTicketEdit.location}/${ticketSelected}`)}
              />
            )}
          {(advancePaymentTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            advancePaymentTicket?.data.ticketStatusId === TicketStatus.New && (
              <Popconfirm
                title={'Bạn chắc chắn hủy phiếu?'}
                onConfirm={() => handleCancel(advancePaymentTicket.data?.id)}
              >
                <BtnCustom type='cancelTicket' isLoading={cancelAdvancePaymentTicketMutation.isLoading} />
              </Popconfirm>
            )}
          {(advancePaymentTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            (advancePaymentTicket?.data.ticketStatusId === TicketStatus.New ||
              advancePaymentTicket?.data.ticketStatusId === TicketStatus.ApprovedRequest) &&
            advancePaymentTicket?.data.isGeneratedReport && (
              <BtnCustom
                type='print'
                onClick={() => handlePrint(advancePaymentTicket.data?.id)}
                isLoading={printAdvancePaymentTicketMutation.isLoading}
              />
            )}
        </Flex>
      );
    }

    if (
      basePath === urlPath.advancePaymentTicketApproval.location &&
      advancePaymentTicket?.data.ticketStatusId === TicketStatus.New
    ) {
      return (
        <Flex gap={10}>
          {advancePaymentTicket.data.waitingApprovalAt && (
            <>
              <Popconfirm
                title={'Bạn chắc chắn phê duyệt?'}
                onConfirm={() =>
                  handleApproval(
                    [{ ticketId: advancePaymentTicket.data.id, step: advancePaymentTicket.data.waitingApprovalAt }],
                    ApprovalStatus.Approve
                  )
                }
              >
                <BtnCustom
                  type='approve'
                  isLoading={
                    approvalAdvancePaymentTicketMutation.variables?.action === ApprovalStatus.Approve &&
                    approvalAdvancePaymentTicketMutation.isLoading
                  }
                />
              </Popconfirm>

              <BtnCustom
                type='reject'
                isLoading={
                  approvalAdvancePaymentTicketMutation.variables?.action === ApprovalStatus.Reject &&
                  approvalAdvancePaymentTicketMutation.isLoading
                }
                onClick={handleReject}
              />
            </>
          )}

          {advancePaymentTicket.data.approvals?.sort((x, y) => x.step - y.step)[
            advancePaymentTicket.data.approvals.length - 1
          ]?.approverId === userInfo?.id && (
            <Popconfirm
              title={'Bạn chắc chắn thu hồi phê duyệt?'}
              onConfirm={() =>
                handleCancelApproval(
                  advancePaymentTicket.data.id,
                  advancePaymentTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    advancePaymentTicket.data.approvals.length - 1
                  ]?.step
                )
              }
            >
              <BtnCustom
                type='cancelApproval'
                stepCancelApproval={
                  advancePaymentTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    advancePaymentTicket.data.approvals.length - 1
                  ]?.step
                }
                isLoading={cancelApprovalTransportationTicketMutation.isLoading}
              />
            </Popconfirm>
          )}
        </Flex>
      );
    }
  };

  const renderOvertimeTicketButtons = () => {
    if (basePath === urlPath.myOvertimeTicket.location) {
      return (
        <Flex gap={10}>
          {(overtimeTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            overtimeTicket?.data.isAvailableEdit && (
              <BtnCustom
                type='edit'
                onClick={() => navigate(`${urlPath.overtimeTicketEdit.location}/${ticketSelected}`)}
              />
            )}
          {(overtimeTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            overtimeTicket?.data.ticketStatusId === TicketStatus.New && (
              <Popconfirm title={'Bạn chắc chắn hủy phiếu?'} onConfirm={() => handleCancel(overtimeTicket.data?.id)}>
                <BtnCustom type='cancelTicket' isLoading={cancelOvertimeTicketMutation.isLoading} />
              </Popconfirm>
            )}
          {(expenseTrackingRequestTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            expenseTrackingRequestTicket?.data.ticketStatusId === TicketStatus.New && (
              <Popconfirm
                title={'Bạn chắc chắn hủy phiếu?'}
                onConfirm={() => handleCancel(expenseTrackingRequestTicket.data?.id)}
              >
                <BtnCustom type='cancelTicket' isLoading={cancelETRTicketMutation.isLoading} />
              </Popconfirm>
            )}
          {(overtimeTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            (overtimeTicket?.data.ticketStatusId === TicketStatus.New ||
              overtimeTicket?.data.ticketStatusId === TicketStatus.ApprovedRequest) &&
            overtimeTicket?.data.isGeneratedReport && (
              <BtnCustom
                type='print'
                onClick={() => handlePrint(overtimeTicket.data?.id)}
                isLoading={printOvertimeTicketMutation.isLoading}
              />
            )}
          {(expenseTrackingRequestTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            (expenseTrackingRequestTicket?.data.ticketStatusId === TicketStatus.New ||
              expenseTrackingRequestTicket?.data.ticketStatusId === TicketStatus.ApprovedRequest) &&
            expenseTrackingRequestTicket?.data.isGeneratedReport && (
              <BtnCustom
                type='print'
                onClick={() => handlePrint(expenseTrackingRequestTicket.data?.id)}
                isLoading={printETRTicketMutation.isLoading}
              />
            )}
          {(overtimeTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            overtimeTicket?.data.ticketStatusId === TicketStatus.Canceled && (
              <Popconfirm
                title={'Bạn chắc chắn sao chép phiếu?'}
                onConfirm={() => handleDuplicate(overtimeTicket.data?.id)}
              >
                <BtnCustom type='duplicate' isLoading={duplicateOvertimeTicketMutation.isLoading} />
              </Popconfirm>
            )}
        </Flex>
      );
    }

    if (
      basePath === urlPath.overtimeTicketApproval.location &&
      overtimeTicket?.data.ticketStatusId === TicketStatus.New
    ) {
      return (
        <Flex gap={10}>
          {overtimeTicket.data.waitingApprovalAt && (
            <Fragment>
              <Popconfirm
                title={'Bạn chắc chắn phê duyệt?'}
                onConfirm={() =>
                  handleApproval(
                    [{ ticketId: overtimeTicket.data.id, step: overtimeTicket.data.waitingApprovalAt }],
                    ApprovalStatus.Approve
                  )
                }
              >
                <BtnCustom
                  type='approve'
                  isLoading={
                    approvalOvertimeTicketMutation.variables?.action === ApprovalStatus.Approve &&
                    approvalOvertimeTicketMutation.isLoading
                  }
                />
              </Popconfirm>

              <BtnCustom
                type='reject'
                onClick={handleReject}
                isLoading={
                  approvalOvertimeTicketMutation.variables?.action === ApprovalStatus.Reject &&
                  approvalOvertimeTicketMutation.isLoading
                }
              />
            </Fragment>
          )}

          {overtimeTicket.data.approvals?.sort((x, y) => x.step - y.step)[overtimeTicket.data.approvals.length - 1]
            ?.approverId === userInfo?.id && (
            <Popconfirm
              title={'Bạn chắc chắn thu hồi phê duyệt?'}
              onConfirm={() =>
                handleCancelApproval(
                  overtimeTicket.data.id,
                  overtimeTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    overtimeTicket.data.approvals.length - 1
                  ]?.step
                )
              }
            >
              <BtnCustom
                type='cancelApproval'
                stepCancelApproval={
                  overtimeTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    overtimeTicket.data.approvals.length - 1
                  ]?.step
                }
                isLoading={cancelApprovalOvertimeTicketMutation.isLoading}
              />
            </Popconfirm>
          )}
        </Flex>
      );
    }

    if (
      basePath === urlPath.expenseTrackingRequestTicketApproval.location &&
      expenseTrackingRequestTicket?.data.ticketStatusId === TicketStatus.New
    ) {
      return (
        <Flex gap={10}>
          {expenseTrackingRequestTicket.data.waitingApprovalAt && (
            <Fragment>
              <Popconfirm
                title={'Bạn chắc chắn phê duyệt?'}
                onConfirm={() =>
                  handleApproval(
                    [
                      {
                        ticketId: expenseTrackingRequestTicket.data.id,
                        step: expenseTrackingRequestTicket.data.waitingApprovalAt
                      }
                    ],
                    ApprovalStatus.Approve
                  )
                }
              >
                <BtnCustom
                  type='approve'
                  isLoading={
                    approvalETRTicketMutation.variables?.action === ApprovalStatus.Approve &&
                    approvalETRTicketMutation.isLoading
                  }
                />
              </Popconfirm>

              <BtnCustom
                type='reject'
                onClick={handleReject}
                isLoading={
                  approvalETRTicketMutation.variables?.action === ApprovalStatus.Reject &&
                  approvalETRTicketMutation.isLoading
                }
              />
            </Fragment>
          )}

          {expenseTrackingRequestTicket.data.approvals?.sort((x, y) => x.step - y.step)[
            expenseTrackingRequestTicket.data.approvals.length - 1
          ]?.approverId === userInfo?.id && (
            <Popconfirm
              title={'Bạn chắc chắn thu hồi phê duyệt?'}
              onConfirm={() =>
                handleCancelApproval(
                  expenseTrackingRequestTicket.data.id,
                  expenseTrackingRequestTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    expenseTrackingRequestTicket.data.approvals.length - 1
                  ]?.step
                )
              }
            >
              <BtnCustom
                type='cancelApproval'
                stepCancelApproval={
                  expenseTrackingRequestTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    expenseTrackingRequestTicket.data.approvals.length - 1
                  ]?.step
                }
                isLoading={cancelApprovalETRTicketMutation.isLoading}
              />
            </Popconfirm>
          )}
        </Flex>
      );
    }

    if (
      basePath === urlPath.hrConfirmOvertimeTicket.location &&
      overtimeTicket?.data.hrConfirmedType === ConfirmTypeEn.WaitingConfirm &&
      overtimeTicket.data.ticketStatus.id === TicketStatus.ApprovedRequest &&
      overtimeTicket?.data?.isSupervisorConfirmed
    ) {
      return (
        <Flex gap={10}>
          <Popconfirm
            title={'Bạn chắc chắn xác nhận?'}
            onConfirm={() => handleHrConfirm(overtimeTicket?.data?.id, ConfirmTypeEn.Confirmed)}
          >
            <BtnCustom
              type='confirm'
              size='small'
              isLoading={
                hrConfirmOvertimeTicketMutation.variables?.confirmType === ConfirmTypeEn.Confirmed &&
                hrConfirmOvertimeTicketMutation.isLoading
              }
            />
          </Popconfirm>
          <BtnCustom
            type='reject'
            size='small'
            onClick={handleHrReject}
            isLoading={
              hrConfirmOvertimeTicketMutation.variables?.confirmType === ConfirmTypeEn.Reject &&
              hrConfirmOvertimeTicketMutation.isLoading
            }
          />
        </Flex>
      );
    }
  };

  const renderBusinessTripTicketButtons = () => {
    if (basePath === urlPath.myBusinessTripTicket.location) {
      return (
        <Flex gap={10}>
          {(businessTripTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            businessTripTicket?.data.isAvailableEdit && (
              <BtnCustom
                type='edit'
                onClick={() => navigate(`${urlPath.businessTripTicketEdit.location}/${ticketSelected}`)}
              />
            )}
          {(businessTripTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            businessTripTicket?.data.ticketStatusId === TicketStatus.New && (
              <Popconfirm
                title={'Bạn chắc chắn hủy phiếu?'}
                onConfirm={() => handleCancel(businessTripTicket.data?.id)}
              >
                <BtnCustom type='cancelTicket' isLoading={cancelBusinessTripTicketMutation.isLoading} />
              </Popconfirm>
            )}
          {(businessTripTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            (businessTripTicket?.data.ticketStatusId === TicketStatus.New ||
              businessTripTicket?.data.ticketStatusId === TicketStatus.ApprovedRequest) &&
            businessTripTicket?.data.isGeneratedReport && (
              <BtnCustom
                type='print'
                onClick={() => handlePrint(businessTripTicket.data?.id)}
                isLoading={printBusinessTripTicketMutation.isLoading}
              />
            )}
          {businessTripTicket?.data.ticketStatusId !== TicketStatus.New && (
            <BtnCustom
              type='addTicketRelate'
              onClick={() => {
                setOpenRelatedTicket({
                  businessTripTicketId: String(businessTripTicket?.data?.id),
                  equipmentSupplyTicketId: '',
                  isOpen: true
                });
              }}
            />
          )}
        </Flex>
      );
    }

    if (
      basePath === urlPath.businessTripTicketApproval.location &&
      businessTripTicket?.data.ticketStatusId === TicketStatus.New
    ) {
      return (
        <Flex gap={10}>
          {businessTripTicket.data.waitingApprovalAt && (
            <>
              <BtnCustom
                type='approve'
                onClick={() =>
                  handleApproval(
                    [{ ticketId: businessTripTicket.data.id, step: businessTripTicket.data.waitingApprovalAt }],
                    ApprovalStatus.Approve
                  )
                }
                isLoading={
                  approvalBusinessTripTicketMutation.variables?.action === ApprovalStatus.Approve &&
                  approvalBusinessTripTicketMutation.isLoading
                }
              />

              <BtnCustom
                type='reject'
                onClick={handleReject}
                isLoading={
                  approvalBusinessTripTicketMutation.variables?.action === ApprovalStatus.Reject &&
                  approvalBusinessTripTicketMutation.isLoading
                }
              />
            </>
          )}

          {businessTripTicket.data.approvals?.sort((x, y) => x.step - y.step)[
            businessTripTicket.data.approvals.length - 1
          ]?.approverId === userInfo?.id && (
            <Popconfirm
              title={'Bạn chắc chắn thu hồi phê duyệt?'}
              onConfirm={() =>
                handleCancelApproval(
                  businessTripTicket.data.id,
                  businessTripTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    businessTripTicket.data.approvals.length - 1
                  ]?.step
                )
              }
            >
              <BtnCustom
                type='cancelApproval'
                stepCancelApproval={
                  businessTripTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    businessTripTicket.data.approvals.length - 1
                  ]?.step
                }
                isLoading={cancelApprovalBusinessTripTicketMutation.isLoading}
              />
            </Popconfirm>
          )}
        </Flex>
      );
    }

    if (
      basePath === urlPath.hrConfirmBusinessTripTicket.location &&
      businessTripTicket?.data.hrConfirmedType === ConfirmTypeEn.WaitingConfirm &&
      businessTripTicket?.data.ticketStatus.id === TicketStatus.ApprovedRequest
    ) {
      return (
        <Flex gap={10}>
          <BtnCustom
            type='confirm'
            size='small'
            onClick={onClick}
            isLoading={
              hrConfirmBusinessTripTicketMutation.variables?.confirmType === ConfirmTypeEn.Confirmed &&
              hrConfirmBusinessTripTicketMutation.isLoading
            }
          />
          <BtnCustom
            type='reject'
            size='small'
            onClick={handleHrReject}
            isLoading={
              hrConfirmBusinessTripTicketMutation.variables?.confirmType === ConfirmTypeEn.Reject &&
              hrConfirmBusinessTripTicketMutation.isLoading
            }
          />
        </Flex>
      );
    }
  };

  const renderPurchaseRequestTicketButtons = () => {
    if (basePath === urlPath.myPurchaseRequestTicket.location) {
      return (
        <Flex gap={10}>
          {(purchaseRequestTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            purchaseRequestTicket?.data.isAvailableEdit && (
              <BtnCustom
                type='edit'
                onClick={() => navigate(`${urlPath.purchaseRequestTicketEdit.location}/${ticketSelected}`)}
              />
            )}
          {(purchaseRequestTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            purchaseRequestTicket?.data.ticketStatusId === TicketStatus.New && (
              <Popconfirm
                title={'Bạn chắc chắn hủy phiếu?'}
                onConfirm={() => handleCancel(purchaseRequestTicket.data?.id)}
              >
                <BtnCustom type='cancelTicket' isLoading={cancelPurchaseRequestTicketMutation.isLoading} />
              </Popconfirm>
            )}
          {(purchaseRequestTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            (purchaseRequestTicket?.data.ticketStatusId === TicketStatus.New ||
              purchaseRequestTicket?.data.ticketStatusId === TicketStatus.ApprovedRequest) &&
            purchaseRequestTicket?.data.isGeneratedReport && (
              <BtnCustom
                type='print'
                onClick={() => handlePrint(purchaseRequestTicket.data?.id)}
                isLoading={printPurchaseRequestTicketMutation.isLoading}
              />
            )}
        </Flex>
      );
    }

    if (
      basePath === urlPath.purchaseRequestTicketApproval.location &&
      purchaseRequestTicket?.data.ticketStatusId === TicketStatus.New
    ) {
      return (
        <Flex gap={10}>
          {purchaseRequestTicket.data.waitingApprovalAt && (
            <>
              <BtnCustom
                type='approve'
                onClick={() =>
                  handleApproval(
                    [{ ticketId: purchaseRequestTicket.data.id, step: purchaseRequestTicket.data.waitingApprovalAt }],
                    ApprovalStatus.Approve
                  )
                }
                isLoading={
                  approvalPurchaseRequestTicketMutation.variables?.action === ApprovalStatus.Approve &&
                  approvalPurchaseRequestTicketMutation.isLoading
                }
              />

              <BtnCustom
                type='reject'
                onClick={handleReject}
                isLoading={
                  approvalPurchaseRequestTicketMutation.variables?.action === ApprovalStatus.Reject &&
                  approvalPurchaseRequestTicketMutation.isLoading
                }
              />
            </>
          )}

          {purchaseRequestTicket.data.approvals?.sort((x, y) => x.step - y.step)[
            purchaseRequestTicket.data.approvals.length - 1
          ]?.approverId === userInfo?.id && (
            <Popconfirm
              title={'Bạn chắc chắn thu hồi phê duyệt?'}
              onConfirm={() =>
                handleCancelApproval(
                  purchaseRequestTicket.data.id,
                  purchaseRequestTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    purchaseRequestTicket.data.approvals.length - 1
                  ]?.step
                )
              }
            >
              <BtnCustom
                type='cancelApproval'
                stepCancelApproval={
                  purchaseRequestTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    purchaseRequestTicket.data.approvals.length - 1
                  ]?.step
                }
                isLoading={cancelApprovalPurchaseRequestTicketMutation.isLoading}
              />
            </Popconfirm>
          )}
        </Flex>
      );
    }
  };

  const renderEquipmentSupplyTicketButtons = () => {
    if (basePath === urlPath.myEquipmentSupplyRequestTicket.location) {
      return (
        <Flex gap={10}>
          {(equipmentSupplyRequestTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            equipmentSupplyRequestTicket?.data.isAvailableEdit && (
              <BtnCustom
                type='edit'
                onClick={() => navigate(`${urlPath.equipmentSupplyRequestTicketEdit.location}/${ticketSelected}`)}
              />
            )}
          {(equipmentSupplyRequestTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            equipmentSupplyRequestTicket?.data.ticketStatusId === TicketStatus.New && (
              <Popconfirm
                title={'Bạn chắc chắn hủy phiếu?'}
                onConfirm={() => handleCancel(equipmentSupplyRequestTicket.data?.id)}
              >
                <BtnCustom type='cancelTicket' isLoading={cancelEquipmentSupplyRequestTicketMutation.isLoading} />
              </Popconfirm>
            )}
          {(equipmentSupplyRequestTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            (equipmentSupplyRequestTicket?.data.ticketStatusId === TicketStatus.New ||
              equipmentSupplyRequestTicket?.data.ticketStatusId === TicketStatus.ApprovedRequest) &&
            equipmentSupplyRequestTicket?.data.isGeneratedReport && (
              <BtnCustom
                type='print'
                onClick={() => handlePrint(equipmentSupplyRequestTicket.data?.id)}
                isLoading={printEquipmentSupplyRequestTicketMutation.isLoading}
              />
            )}
          {equipmentSupplyRequestTicket?.data.ticketStatusId === TicketStatus.New && (
            <BtnCustom
              type='addTicketRelate'
              onClick={() => {
                setOpenRelatedTicket({
                  businessTripTicketId: '',
                  equipmentSupplyTicketId: String(equipmentSupplyRequestTicket.data.id),
                  isOpen: true
                });
              }}
            />
          )}
        </Flex>
      );
    }

    if (
      basePath === urlPath.equipmentSupplyRequestTicketApproval.location &&
      equipmentSupplyRequestTicket?.data.ticketStatusId === TicketStatus.New
    ) {
      return (
        <Flex gap={10}>
          {equipmentSupplyRequestTicket.data.waitingApprovalAt && (
            <>
              <BtnCustom
                type='approve'
                onClick={() =>
                  handleApproval(
                    [
                      {
                        ticketId: equipmentSupplyRequestTicket.data.id,
                        step: equipmentSupplyRequestTicket.data.waitingApprovalAt
                      }
                    ],
                    ApprovalStatus.Approve
                  )
                }
                isLoading={
                  approvalEquipmentSupplyRequestTicketMutation.variables?.action === ApprovalStatus.Approve &&
                  approvalEquipmentSupplyRequestTicketMutation.isLoading
                }
              />

              <BtnCustom
                type='reject'
                onClick={handleReject}
                isLoading={
                  approvalEquipmentSupplyRequestTicketMutation.variables?.action === ApprovalStatus.Reject &&
                  approvalEquipmentSupplyRequestTicketMutation.isLoading
                }
              />
            </>
          )}

          {equipmentSupplyRequestTicket.data.approvals?.sort((x, y) => x.step - y.step)[
            equipmentSupplyRequestTicket.data.approvals.length - 1
          ]?.approverId === userInfo?.id && (
            <Popconfirm
              title={'Bạn chắc chắn thu hồi phê duyệt?'}
              onConfirm={() =>
                handleCancelApproval(
                  equipmentSupplyRequestTicket.data.id,
                  equipmentSupplyRequestTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    equipmentSupplyRequestTicket.data.approvals.length - 1
                  ]?.step
                )
              }
            >
              <BtnCustom
                type='cancelApproval'
                stepCancelApproval={
                  equipmentSupplyRequestTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    equipmentSupplyRequestTicket.data.approvals.length - 1
                  ]?.step
                }
                isLoading={cancelApprovalEquipmentSupplyRequestTicketMutation.isLoading}
              />
            </Popconfirm>
          )}
        </Flex>
      );
    }
  };

  const renderAdditionalWorkingTimeTicketButtons = () => {
    if (basePath === urlPath.myAdditionalWorkingTimeTicket.location) {
      return (
        <Flex gap={10}>
          {(additionalWorkingTimeTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            additionalWorkingTimeTicket?.data.isAvailableEdit && (
              <BtnCustom
                type='edit'
                onClick={() => navigate(`${urlPath.additionalWorkingTimeTicketEdit.location}/${ticketSelected}`)}
              />
            )}
          {(additionalWorkingTimeTicket?.data.createdById === userInfo?.id || isUserAdmin) &&
            additionalWorkingTimeTicket?.data.ticketStatusId === TicketStatus.New && (
              <Popconfirm
                title={'Bạn chắc chắn hủy phiếu?'}
                onConfirm={() => handleCancel(additionalWorkingTimeTicket.data?.id)}
              >
                <BtnCustom type='cancelTicket' isLoading={cancelAdditionalWorkingTimeTicketMutation.isLoading} />
              </Popconfirm>
            )}
        </Flex>
      );
    }

    if (
      basePath === urlPath.additionalWorkingTimeTicketApproval.location &&
      additionalWorkingTimeTicket?.data.ticketStatusId === TicketStatus.New
    ) {
      return (
        <Flex gap={10}>
          {additionalWorkingTimeTicket.data.waitingApprovalAt && (
            <>
              <BtnCustom
                type='approve'
                onClick={() =>
                  handleApproval(
                    [
                      {
                        ticketId: additionalWorkingTimeTicket.data.id,
                        step: additionalWorkingTimeTicket.data.waitingApprovalAt
                      }
                    ],
                    ApprovalStatus.Approve
                  )
                }
                isLoading={
                  approvalAdditionalWorkingTimeTicketMutation.variables?.action === ApprovalStatus.Approve &&
                  approvalAdditionalWorkingTimeTicketMutation.isLoading
                }
              />
              <BtnCustom
                type='reject'
                onClick={handleReject}
                isLoading={
                  approvalAdditionalWorkingTimeTicketMutation.variables?.action === ApprovalStatus.Reject &&
                  approvalAdditionalWorkingTimeTicketMutation.isLoading
                }
              />
            </>
          )}

          {additionalWorkingTimeTicket.data.approvals?.sort((x, y) => x.step - y.step)[
            additionalWorkingTimeTicket.data.approvals.length - 1
          ]?.approverId === userInfo?.id && (
            <Popconfirm
              title={'Bạn chắc chắn thu hồi phê duyệt?'}
              onConfirm={() =>
                handleCancelApproval(
                  additionalWorkingTimeTicket.data.id,
                  additionalWorkingTimeTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    additionalWorkingTimeTicket.data.approvals.length - 1
                  ]?.step
                )
              }
            >
              <BtnCustom
                type='cancelApproval'
                stepCancelApproval={
                  additionalWorkingTimeTicket.data.approvals?.sort((x, y) => x.step - y.step)[
                    additionalWorkingTimeTicket.data.approvals.length - 1
                  ]?.step
                }
                isLoading={cancelApprovalAdditionalWorkingTimeTicketMutation.isLoading}
              />
            </Popconfirm>
          )}
        </Flex>
      );
    }
  };

  const handleRenderButton = () => {
    if (!ticketSelected) return;

    const renderFunctions = [
      renderLeaveTicketButtons,
      renderTransportationTicketButtons,
      renderAdvancePaymentTicketButtons,
      renderOvertimeTicketButtons,
      renderBusinessTripTicketButtons,
      renderPurchaseRequestTicketButtons,
      renderEquipmentSupplyTicketButtons,
      renderAdditionalWorkingTimeTicketButtons,
      renderHrConfirmButtons,
      renderResignationTicketButtons
    ];

    for (const renderFunction of renderFunctions) {
      const result = renderFunction();
      if (result) return result;
    }
  };

  return (
    <Fragment>
      <Flex className='row-handle' justify='space-between' vertical={false}>
        <Icon url={Close} className='btn-close' size={25} onClick={() => handleCloseDetail()} />
        <div className='btn-handle'>{handleRenderButton()}</div>
      </Flex>

      {openReplacement.isOpen && (
        <AddReplacement
          departmentId={leaveTicket?.data.leaveTicketDetails?.[0]?.employee?.departmentId}
          employeeId={leaveTicket?.data.leaveTicketDetails?.[0]?.employeeId}
          replacerId={leaveTicket?.data?.replacerId}
          open={openReplacement}
          setOpen={setOpenReplacement}
          handleAddReplacement={handleAddReplacement}
        />
      )}

      {openRejectNote && (
        <AddRejectNote open={openRejectNote} setOpen={setOpenRejectNote} handleAddNote={handleAddRejectNote} />
      )}

      {openApproveNote && (
        <AddApproveNote
          open={openApproveNote}
          setOpen={setOpenApproveNote}
          handleAddNote={handleAddApproveNote}
          leaveTicket={leaveTicket?.data}
        />
      )}
      {openHrConfirmNote && (
        <AddApproveNote
          open={openHrConfirmNote}
          setOpen={setOpenHrConfirmNote}
          handleAddNote={handleAddHrConfirmNote}
          resignationTicket={resignationTicket?.data}
        />
      )}
      {openHrRejectNote && (
        <AddRejectNote open={openHrRejectNote} setOpen={setOpenHrRejectNote} handleAddNote={handleAddHrRejectNote} />
      )}

      {openConfirmTransportation.isOpen && (
        <ConfirmTransportation
          open={openConfirmTransportation}
          setOpen={setOpenConfirmTransportation}
          handleConfirmTransportation={handleConfirmTransportation}
        />
      )}

      {openNoConfirmNoteTransportation && (
        <AddRejectNote
          open={openNoConfirmNoteTransportation}
          setOpen={setOpenNoConfirmNoteTransportation}
          handleAddNote={handleAddNoConfirmNoteTransportation}
        />
      )}

      {openHrConfirmTransportation.isOpen && (
        <HrConfirmTransportation
          open={openHrConfirmTransportation}
          setOpen={setOpenHrConfirmTransportation}
          handleConfirmTransportation={handleHrConfirmTransportation}
        />
      )}

      {openHrNoConfirmNoteTransportation && (
        <AddRejectNote
          open={openHrNoConfirmNoteTransportation}
          setOpen={setOpenHrNoConfirmNoteTransportation}
          handleAddNote={handleAddHrNoConfirmNoteTransportation}
        />
      )}

      {openEditTransportation.isOpen && (
        <EditTransportationTicket open={openEditTransportation} setOpen={setOpenEditTransportation} />
      )}

      {openRelatedTicket.isOpen && <AddRelatedTicket open={openRelatedTicket} setOpen={setOpenRelatedTicket} />}
    </Fragment>
  );
};

export default BtnHrHandle;
